/* ea-klaviyo-subscribe.js
 * Subscribes an email to a Klaviyo list via the client-side
 * "Create Client Subscription" endpoint.
 *
 * Wire any <form> with [data-ea-klaviyo-form]. The handler reads the
 * target list and any extra profile properties from data-* attributes.
 *
 * Properties can come from three places, later ones overriding earlier:
 *   1. data-prop-* on the <form> itself (e.g. footer cat capture)
 *   2. a selected <option> in a <select data-ea-klaviyo-select>
 *   3. any CHECKED radio/checkbox inside the form that carries data-prop-*
 *      (this is how the pet popup sets dog_interested / cat_interested)
 *
 * Load it once globally (theme.liquid before </body>), OR from the section
 * itself -- the init guard below makes it safe to include more than once.
 */
(function () {
  // Guard: if the listener is already bound, don't bind it again.
  if (window.__eaKlaviyoSubscribeInit) return;
  window.__eaKlaviyoSubscribeInit = true;

  // Public API key / Site ID. Safe to expose client-side. (Settings > Account > API Keys)
  var KLAVIYO_PUBLIC_KEY = 'HqciEH';
  var KLAVIYO_REVISION = '2026-04-15';
  var ENDPOINT =
    'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY;

  function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }

  // Any data-prop-foo="bar" on an element becomes a profile property { foo: "bar" }.
  // "true"/"false" are coerced to booleans so dog_interested / cat_interested
  // land as real booleans.
  function readPropsFrom(el, into) {
    Array.prototype.forEach.call(el.attributes, function (attr) {
      if (attr.name.indexOf('data-prop-') === 0) {
        var key = attr.name.slice('data-prop-'.length);
        var val = attr.value;
        if (val === 'true') val = true;
        else if (val === 'false') val = false;
        into[key] = val;
      }
    });
    return into;
  }

  // Resolves the target list + properties. Precedence (low -> high):
  //   form attrs  <  selected <option>  <  checked radios/checkboxes
  function resolveRouting(form) {
    var listId = form.getAttribute('data-list-id');
    var properties = readPropsFrom(form, {});

    var select = form.querySelector('[data-ea-klaviyo-select]');
    if (select) {
      var opt = select.options[select.selectedIndex];
      if (opt) {
        if (opt.getAttribute('data-list-id')) listId = opt.getAttribute('data-list-id');
        readPropsFrom(opt, properties); // option props win over form props
      }
    }

    // Checked radios/checkboxes carrying data-prop-* (the pet chooser).
    // A single "Both" radio can carry both data-prop-dog_interested and
    // data-prop-cat_interested, so it sets both booleans at once.
    var checked = form.querySelectorAll(
      'input[type="radio"]:checked, input[type="checkbox"]:checked'
    );
    Array.prototype.forEach.call(checked, function (el) {
      readPropsFrom(el, properties); // checked-input props win last
    });

    return { listId: listId, properties: properties };
  }

  function setStatus(form, message, state) {
    var el = form.querySelector('[data-ea-status]');
    if (!el) return;
    el.textContent = message;
    el.setAttribute('data-state', state || '');
  }

  async function subscribe(form) {
    var emailInput = form.querySelector('input[type="email"]');
    var email = ((emailInput && emailInput.value) || '').trim();
    var honeypot = form.querySelector('[data-ea-hp]');

    if (honeypot && honeypot.value) return; // silently drop bots
    if (!isEmail(email)) {
      setStatus(form, 'Please enter a valid email address.', 'error');
      return;
    }

    var routing = resolveRouting(form);
    var listId = routing.listId;
    var source = form.getAttribute('data-source') || 'Website';
    var properties = routing.properties;

    // Optional: require a pet selection. Set data-require-selection on the form
    // and list the property keys that satisfy it in data-selection-keys
    // (comma-separated). Native radio "required" also enforces this, but this
    // is a JS backstop in case the markup ever changes.
    if (form.getAttribute('data-require-selection') === 'true') {
      var keys = (form.getAttribute('data-selection-keys') || '')
        .split(',')
        .map(function (k) { return k.trim(); })
        .filter(Boolean);
      var hasOne = keys.some(function (k) { return properties[k] === true; });
      if (keys.length && !hasOne) {
        setStatus(form, 'Please choose dog, cat, or both.', 'error');
        return;
      }
    }

    if (!listId) {
      console.error('Klaviyo subscribe: no list ID resolved for this form/selection.');
      setStatus(form, 'Something went wrong. Please try again.', 'error');
      return;
    }

    var profileAttributes = {
      email: email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    if (Object.keys(properties).length) profileAttributes.properties = properties;

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source, // shows on the consent record; optional, drop if it 400s
          profile: { data: { type: 'profile', attributes: profileAttributes } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };

    var btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
    setStatus(form, 'Signing you up...', 'pending');

    try {
      var res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      });

      // 202 = accepted. With a double opt-in list, the confirmation email
      // is what actually completes the subscription.
      if (res.status === 202) {
        setStatus(
          form,
          form.getAttribute('data-success-message') || "Thanks. You're on the list.",
          'success'
        );
        form.reset();
        // Let a host (e.g. the popup) react to a successful signup.
        form.dispatchEvent(new CustomEvent('ea:subscribed', { bubbles: true }));
      } else {
        var detail = await res.text();
        console.error('Klaviyo subscribe failed', res.status, detail);
        setStatus(form, 'Something went wrong. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Klaviyo subscribe error', err);
      setStatus(form, 'Something went wrong. Please try again.', 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-ea-klaviyo-form]');
    if (!form) return;
    e.preventDefault();
    subscribe(form);
  });
})();